import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import moment from 'moment-timezone';
import { MembersService } from '../../../../services/members/members.service';
import { AutopayActivatorComponent, AutopayActivatorDialogInput, AutopayActivatorDialogOutput } from './autopay-activator/autopay-activator.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GrowlerService } from '../../../../services/growler.service';
import { Membership } from '../../../../models/entities/membership';
import { MatDialog } from '@angular/material/dialog';
import { AutopayDisableConfirmationComponent, AutopayDisableDialogInput } from './autopay-disable-confirmation/autopay-disable-confirmation.component';
import Moment = moment.Moment;

@UntilDestroy()
@Component({
    selector: 'lf-autopay',
    styleUrls: ['./autopay.scss'],
    templateUrl: './autopay.component.html',
})
export class AutopayComponent implements OnInit
{
    @Input() doAutoOpen = false;

    @Output() enabled: EventEmitter<boolean> = new EventEmitter<boolean>();

    memberships: Membership[] = [];
    autopayMemberIndexUnderEdit: number = null;
    nextAutopay: Moment;
    isLoading = true;

    constructor(
            private _matDialogService: MatDialog,
            private _membersService: MembersService,
            private _growlerService: GrowlerService,
    ) {
    }

    ngOnInit() {
        this._membersService
                .getUserMemberships(null)
                .pipe(untilDestroyed(this))
                .subscribe(
                        (response: Membership[]) => {
                            this.memberships = response.filter((m: any) => {
                                return m.organization !== null;
                            });
                            this.isLoading = false;

                            if (
                                    this.doAutoOpen &&
                                    this.memberships.length === 1 &&
                                    !this.memberships[0].profile.autopayEnabled
                            ) {
                                this.autopayMemberIndexUnderEdit = 0;
                                this.showEnableAutopayModal();
                            }
                        },
                        () => {
                            this.isLoading = false;
                            this._growlerService.error('Error', 'Unable to load user\'s memberships and autopay settings');
                            this.autopayMemberIndexUnderEdit = null;
                        },
                );

        this.determineNextAutopayTime();
    }

    // noinspection JSMethodCanBeStatic
    determineNextAutopayTime() {
        const now = moment.utc();
        const runTimes = [
            moment.tz('America/New_York')
                    .hour(7)
                    .minute(0)
                    .second(0)
                    .millisecond(0),
            moment.tz('America/New_York')
                    .hour(14)
                    .minute(0)
                    .second(0)
                    .millisecond(0),
        ];

        // Run for dates today.
        for (const runTime of runTimes) {
            if (now > runTime) {
                runTime.add(1, 'day');
            } else {
                this.nextAutopay = runTime;
                return;
            }
        }

        // If still no hit found, times were adjusted for tomorrow, check again.
        for (const runTime of runTimes) {
            if (now > runTime) {
                runTime.add(1, 'day');
            } else {
                this.nextAutopay = runTime;
                return;
            }
        }
    }

    autopayChanged(membershipIndex: number, checked: boolean) {
        this.memberships[membershipIndex].profile.autopayEnabled = checked;
        this.autopayMemberIndexUnderEdit = membershipIndex;

        // Disable case
        if (!checked) {
            this.showDisableAutopayConfirmation();
            return;
        }

        this.showEnableAutopayModal();
    }

    disableAutopayCanceled() {
        this.memberships[
                this.autopayMemberIndexUnderEdit
                ].profile.autopayEnabled = true;
        this.autopayMemberIndexUnderEdit = null;
    }

    enableAutopayCanceled() {
        this.memberships[
                this.autopayMemberIndexUnderEdit
                ].profile.autopayEnabled = false;
        this.autopayMemberIndexUnderEdit = null;
    }

    autopayEnabled($event: any) {
        const membership = this.memberships[this.autopayMemberIndexUnderEdit];
        membership.profile.autopayEnabled = true;
        membership.profile.autopayMax = $event.profile.autopayMax;
        membership.profile.paymentMethodId = $event.profile.paymentMethodId;
        membership.profile.paymentMethodType = $event.profile.paymentMethodType;
        membership.profile.paymentMethod = $event.profile.paymentMethod;

        this.enabled.emit(true);

        this.autopayMemberIndexUnderEdit = null;
    }

    showEnableAutopayModal() {
        this._matDialogService.open(AutopayActivatorComponent, {
            data: <AutopayActivatorDialogInput>{
                organization: this.memberships[this.autopayMemberIndexUnderEdit].organization,
            },
            width: '560px',
        }).afterClosed()
                .pipe(untilDestroyed(this))
                .subscribe((result: AutopayActivatorDialogOutput) => {
                    if (result && result.data) {
                        // todo - define data as a type; is it a Membership?
                        this.autopayEnabled(result.data);
                    } else {
                        this.enableAutopayCanceled();
                    }
                });
    }

    showDisableAutopayConfirmation() {
        this._matDialogService.open(AutopayDisableConfirmationComponent, {
            data: <AutopayDisableDialogInput>{
                organization: this.memberships[this.autopayMemberIndexUnderEdit].organization,
            },
            width: '555px',
        }).afterClosed()
                .pipe(untilDestroyed(this))
                .subscribe((success: boolean) => {
                    // TODO - use generic return type from branch once it is in master
                    if (success) {
                        this.memberships[this.autopayMemberIndexUnderEdit].profile.autopayEnabled = false;
                        this.autopayMemberIndexUnderEdit = null;
                        this.enabled.emit(false);
                    } else {
                        this.disableAutopayCanceled();
                    }
                });
    }
}
